/**
 * Multi-phase step-up authentication modal.
 * State machine: TRYING_WITHOUT_PASSWORD → PASSWORD → CHOOSE → CHALLENGE → VERIFIED
 *
 * Ported from shipstation-lite/src/features/mfa/step-up-auth-modal.tsx, widened
 * with the feature gaps ss-ui-client needs (G1 isMfaSettings, G2 messageSet,
 * G3 title, G4 phoneVerificationState, G5 consents, G7 onResendCode,
 * G9 onTokenTimeout, R3 zIndex). Every added prop is optional and defaults to
 * shipstation-lite's current behaviour.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader, Modal } from '@mantine/core';
import { useIntl } from 'react-intl';

import { ChallengePhase } from './challenge-phase';
import { ChoosePhase } from './choose-phase';
import { getMfaErrorDetail } from './mfa-error-detail';
import { PasswordPhase } from './password-phase';
import { ModalContent } from '../../components/modal-content';
import { startMfaChallenge, startStepUpAuth, submitMfaChallenge } from '../mfa-api';
import {
  CHALLENGE_TYPES,
  mapManagementApiTypeToAuthenticationApiChallengeType,
  MFA_ANALYTICS_EVENTS,
  MFA_MODAL_DEFAULT_Z_INDEX,
  PHASES,
} from '../mfa-constants';
import { checkIsTokenTimeoutError, hashConsents } from '../mfa-helpers';
import { mfaMessages } from '../mfa-messages';
import { getMfaTracker } from '../mfa-platform';

import type { MfaErrorDetail } from './mfa-error-detail';
import type { Phase, PhoneVerificationState } from '../mfa-constants';
import type { MfaMessageSet } from '../mfa-messages';
import type { HashedMfaConsent, MfaConsent, MfaEnrollment, StartStepUpAuthResponse } from '../mfa-types';
import type { MessageDescriptor } from 'react-intl';

export type StepUpAuthModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onVerified: (accessToken: string) => void;
  username: string;
  passwordResetHref: string;

  /** G3 — defaults to `mfaMessages.mfaModalTitle`. */
  title?: MessageDescriptor;
  /** G2 — defaults to `mfaMessageSets.lastMfaClaimMessages` (resolved by `PasswordPhase`). */
  messageSet?: MfaMessageSet;
  /** G1 — changes the claims on the returned token. */
  isMfaSettings?: boolean;
  /** G4 — drives contextual copy in the password and choose phases. */
  phoneVerificationState?: PhoneVerificationState;
  /** G5 — hashed and sent with the challenge submission. */
  consents?: MfaConsent[];
  /** G5 — appended (with a leading space) to each consent before hashing. */
  consentDisclaimer?: MessageDescriptor;
  /** G5 — sent with the challenge submission for phone-number validation. */
  userPhone?: string;
  /** G7 — replaces the internal resend handler. */
  onResendCode?: () => Promise<void>;
  /** G9 — called in addition to the internal reset when the mfaToken expires. */
  onTokenTimeout?: () => void;
  isUnskippable?: boolean;
  /** R3 — ss-ui-client opens this from inside another modal and passes `useMaxZ()`. */
  zIndex?: number;
  /** Request URL or feature name that triggered step-up, forwarded to analytics. */
  source?: string;
  /** G6 — ss-ui-client exposes the loaded enrollments on its step-up auth context. */
  onEnrollmentsLoaded?: (enrollments: MfaEnrollment[]) => void;
};

export const StepUpAuthModal = ({
  isOpen,
  onClose,
  onVerified,
  username,
  passwordResetHref,
  title,
  messageSet,
  isMfaSettings = false,
  phoneVerificationState,
  consents,
  consentDisclaimer,
  userPhone,
  onResendCode,
  onTokenTimeout,
  isUnskippable = false,
  zIndex = MFA_MODAL_DEFAULT_Z_INDEX,
  source = '',
  onEnrollmentsLoaded,
}: StepUpAuthModalProps) => {
  const intl = useIntl();
  const [phase, setPhase] = useState<Phase>(PHASES.TRYING_WITHOUT_PASSWORD);
  const [enrollments, setEnrollments] = useState<MfaEnrollment[]>([]);
  const [mfaToken, setMfaToken] = useState('');
  const [oobCode, setOobCode] = useState('');
  const [challengedEnrollment, setChallengedEnrollment] = useState<MfaEnrollment | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<MfaErrorDetail | null>(null);
  const hasAttemptedPasswordless = useRef(false);

  const startChallenge = useCallback(
    async (enrollment: MfaEnrollment, token: string) => {
      setError(null);
      setIsSubmitting(true);

      try {
        const challengeType = mapManagementApiTypeToAuthenticationApiChallengeType(enrollment.type);
        const challengeRes = await startMfaChallenge({
          authenticatorId: enrollment.id,
          challengeType,
          isMfaSettings,
          mfaToken: token,
        });

        setChallengedEnrollment(enrollment);
        setMfaToken(challengeRes.mfaToken ?? token);
        setOobCode(challengeRes.oobCode ?? '');
        setPhase(PHASES.CHALLENGE);
        getMfaTracker()(MFA_ANALYTICS_EVENTS.codeChallenge, { source });
      } catch (err) {
        setError(getMfaErrorDetail(err, ''));
        setPhase(PHASES.CHOOSE);
      } finally {
        setIsSubmitting(false);
      }
    },
    [isMfaSettings, source],
  );

  /** Route a step-up auth response to the correct next phase. */
  const handleStepUpResponse = useCallback(
    async (resData: StartStepUpAuthResponse) => {
      const nextEnrollments = resData.enrollments ?? [];
      const nextMfaToken = resData.mfaToken ?? '';

      setEnrollments(nextEnrollments);
      setMfaToken(nextMfaToken);
      onEnrollmentsLoaded?.(nextEnrollments);

      if (!resData.isMfaRequired) {
        setPhase(PHASES.VERIFIED);
        onVerified(nextMfaToken);
        return;
      }

      const [onlyEnrollment] = nextEnrollments;
      if (nextEnrollments.length === 1 && onlyEnrollment) {
        await startChallenge(onlyEnrollment, nextMfaToken);
        return;
      }

      setPhase(PHASES.CHOOSE);
    },
    [onEnrollmentsLoaded, onVerified, startChallenge],
  );

  useEffect(() => {
    if (!isOpen || phase !== PHASES.TRYING_WITHOUT_PASSWORD) {
      return;
    }

    // Prevent double-fire in StrictMode
    if (hasAttemptedPasswordless.current) {
      return;
    }
    hasAttemptedPasswordless.current = true;

    const attemptPasswordlessStepUp = async () => {
      try {
        const resData = await startStepUpAuth({ isMfaSettings, tryWithoutPassword: true });
        await handleStepUpResponse(resData);
      } catch {
        setPhase(PHASES.PASSWORD);
      }
    };

    attemptPasswordlessStepUp();
  }, [handleStepUpResponse, isMfaSettings, isOpen, phase]);

  // Reset state when the modal closes
  useEffect(() => {
    if (isOpen) {
      return;
    }

    setPhase(PHASES.TRYING_WITHOUT_PASSWORD);
    setEnrollments([]);
    setMfaToken('');
    setOobCode('');
    setChallengedEnrollment(null);
    setIsSubmitting(false);
    setError(null);
    hasAttemptedPasswordless.current = false;
  }, [isOpen]);

  const handlePasswordSubmit = async (password: string) => {
    setError(null);
    setIsSubmitting(true);

    try {
      const resData = await startStepUpAuth({ isMfaSettings, password });
      getMfaTracker()(MFA_ANALYTICS_EVENTS.passwordChallenge, { source });
      await handleStepUpResponse(resData);
    } catch (err) {
      setError(getMfaErrorDetail(err, ''));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEnrollmentSelect = (enrollment: MfaEnrollment) => {
    startChallenge(enrollment, mfaToken);
  };

  const handleTokenTimeout = () => {
    hasAttemptedPasswordless.current = false;
    setMfaToken('');
    setChallengedEnrollment(null);
    setError(null);
    setPhase(PHASES.TRYING_WITHOUT_PASSWORD);
    onTokenTimeout?.();
  };

  const hashConsentsForSubmit = async (): Promise<HashedMfaConsent[] | undefined> => {
    if (!consents || consents.length === 0) {
      return undefined;
    }

    const disclaimerSuffix = consentDisclaimer ? ` ${intl.formatMessage(consentDisclaimer)}` : '';

    try {
      return await hashConsents(consents, disclaimerSuffix);
    } catch {
      // Submitting with no consents matches the legacy provider (challenge.jsx:199-202 on master).
      // Changing this behaviour is a product decision, not a port decision.
      return undefined;
    }
  };

  const handleCodeSubmit = async (code: string) => {
    if (!challengedEnrollment) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const challengeType = mapManagementApiTypeToAuthenticationApiChallengeType(challengedEnrollment.type);
      const codeField = challengeType === CHALLENGE_TYPES.OOB ? { bindingCode: code } : { otpCode: code };
      const hashedConsents = await hashConsentsForSubmit();

      const { accessToken } = await submitMfaChallenge({
        ...codeField,
        challengeType,
        consents: hashedConsents,
        isMfaSettings,
        mfaToken,
        oobCode,
        userPhone,
      });

      setPhase(PHASES.VERIFIED);
      onVerified(accessToken);
    } catch (err) {
      if (checkIsTokenTimeoutError(err)) {
        handleTokenTimeout();
        return;
      }
      setError(getMfaErrorDetail(err, intl.formatMessage(mfaMessages.invalidCodeError)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!challengedEnrollment) {
      return;
    }

    if (onResendCode) {
      await onResendCode();
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      const challengeType = mapManagementApiTypeToAuthenticationApiChallengeType(challengedEnrollment.type);
      const challengeRes = await startMfaChallenge({
        authenticatorId: challengedEnrollment.id,
        challengeType,
        isMfaSettings,
        mfaToken,
      });

      setMfaToken(challengeRes.mfaToken ?? mfaToken);
      setOobCode(challengeRes.oobCode ?? '');
    } catch (err) {
      setError(getMfaErrorDetail(err, intl.formatMessage(mfaMessages.resendCodeError)));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChooseDifferentMethod = () => {
    setChallengedEnrollment(null);
    setError(null);
    setPhase(PHASES.CHOOSE);
  };

  const handleClose = () => {
    if (isUnskippable) {
      return;
    }
    onClose();
  };

  const shouldShowModal = isOpen && phase !== PHASES.VERIFIED;
  const modalTitle = intl.formatMessage(title ?? mfaMessages.mfaModalTitle);

  return (
    <Modal
      centered
      closeOnClickOutside={false}
      closeOnEscape={!isUnskippable}
      onClose={handleClose}
      opened={shouldShowModal}
      title={modalTitle}
      withCloseButton={!isUnskippable}
      zIndex={zIndex}
    >
      {phase === PHASES.TRYING_WITHOUT_PASSWORD ? (
        <ModalContent align='center'>
          <Loader />
        </ModalContent>
      ) : null}

      {phase === PHASES.PASSWORD ? (
        <PasswordPhase
          errorCode={error?.code}
          errorMessage={error?.message}
          isSubmitting={isSubmitting}
          isUnskippable={isUnskippable}
          messageSet={messageSet}
          onSubmit={handlePasswordSubmit}
          passwordResetHref={passwordResetHref}
          phoneVerificationState={phoneVerificationState}
          username={username}
        />
      ) : null}

      {phase === PHASES.CHOOSE ? (
        <ChoosePhase
          enrollments={enrollments}
          errorCode={error?.code}
          errorMessage={error?.message}
          isSubmitting={isSubmitting}
          onSelect={handleEnrollmentSelect}
          phoneVerificationState={phoneVerificationState}
        />
      ) : null}

      {phase === PHASES.CHALLENGE && challengedEnrollment ? (
        <ChallengePhase
          enrollment={challengedEnrollment}
          errorCode={error?.code}
          errorMessage={error?.message}
          hasMultipleEnrollments={enrollments.length > 1}
          isSubmitting={isSubmitting}
          onChooseDifferentMethod={handleChooseDifferentMethod}
          onResendCode={handleResendCode}
          onSubmit={handleCodeSubmit}
        />
      ) : null}
    </Modal>
  );
};
